import { useEffect, useState, useContext, useRef } from 'react'
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  Autocomplete,
  TextField,
  Divider,
  Box,
  IconButton
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import DeleteDialog from '../../DeleteDialog'
import { authContext } from '../../../contexts/AuthContext'
import {
  listSocietyCompliances,
  updateSocietyCompliance,
  deleteSocietyCompliance,
  listUnassignedCompliances,
  addComplianceToSociety
} from '../../../services/societyCompliances'
import ComplianceEditDialog from './ComplianceEditDialog'
import SectionTitle from '../../SectionTitle'
import SocietyComplianceChart from './SocietyComplianceChart'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import CPEditIconButton from '../../Utils/CPEditIconButton'
import CPDeleteIconButton from '../../Utils/CPDeleteIconButton'
import SocietyCompletionChart from './SocietyCompletionChart'
import DescriptionIcon from '@mui/icons-material/Description'
import SocietyComplianceDocuments from './SocietyComplianceDocuments'
import { insertComplianceForSociety } from '../../../services/societyCompliances'
import ComplianceDialog from '../Compliances/ComplianceDialog'

export default function SocietyCompliances({ society, adminMode = false }) {
  const { t } = useTranslation()
  const { auth } = useContext(authContext)
  const [compliances, setCompliances] = useState([])
  const [deleteItem, setDeleteItem] = useState({})
  const [editItem, setEditItem] = useState({})

  const [availableCompliances, setAvailableCompliances] = useState([])
  const [selectedCompliance, setSelectedCompliance] = useState(null)

  const [documentsItem, setDocumentsItem] = useState({})
  const [addNewCompliance, setAddNewCompliance] = useState(false)
  const [newComplianceData, setNewComplianceData] = useState({})

  const exportRef = useRef(null)

  useEffect(() => {
    fetchData()
  }, [society.id])

  useEffect(() => {
    if (adminMode) {
      fetchAvailableCompliances()
    }
  }, [society.id, compliances])

  const fetchAvailableCompliances = async () => {
    try {
      const data = await listUnassignedCompliances(society.id)
      setAvailableCompliances(data)
    } catch (err) {
      console.error('Error loading unassigned compliances:', err)
    }
  }

  const handleAssign = async () => {
    if (!selectedCompliance) return
    try {
      await addComplianceToSociety(
        society.id,
        selectedCompliance.id,
        auth.user.id
      )
      await fetchData()
      await fetchAvailableCompliances()
      setSelectedCompliance(null)
    } catch (err) {
      console.error('Error assigning compliance:', err)
    }
  }

  const fetchData = async () => {
    try {
      const data = await listSocietyCompliances(society.id)
      setCompliances(data)
    } catch (err) {
      console.error('Error loading compliances:', err)
    }
  }

  const handleDelete = async (item) => {
    try {
      await deleteSocietyCompliance(item.id, auth.user.id)
      await fetchData()
    } catch (err) {
      console.error('Error deleting society compliance:', err)
    }
    setDeleteItem({})
  }

  const handleUpdate = async (item) => {
    setCompliances((prev) =>
      prev.map((c) =>
        c.id === item.id
          ? { ...c, status: item.status, due_date: item.due_date }
          : c
      )
    )
    setEditItem({})

    try {
      await updateSocietyCompliance({
        id: item.id,
        status: item.status,
        due_date: item.due_date || null,
        notifyClient: !!item.notify_client,
        loggedUserId: auth.user.id
      })
      await fetchData()
    } catch (err) {
      console.error('Error updating society compliance:', err)
      await fetchData()
    }
    setEditItem({})
  }

  // Exportar a PDF
  const handleExportPDF = async (e) => {
    e.currentTarget.blur()

    const btn = e.currentTarget
    btn.classList.add('no-export')

    const input = exportRef.current
    if (!input) return

    const canvas = await html2canvas(input, { scale: 2, useCORS: true })
    const imgData = canvas.toDataURL('image/png')

    const pdf = new jsPDF('p', 'mm', 'a4')
    const pageWidth = pdf.internal.pageSize.getWidth()
    const imgProps = pdf.getImageProperties(imgData)
    const pdfHeight = (imgProps.height * pageWidth) / imgProps.width

    pdf.addImage(imgData, 'PNG', 0, 0, pageWidth, pdfHeight)
    pdf.save(`society_${society.legal_id}_compliances.pdf`)

    btn.classList.remove('no-export')
  }

  const handleCreateAndAssign = async () => {
    try {
      const params = {
        ...newComplianceData,
        society_id: society.id,
        loggedUserId: auth.user.id
      }

      await insertComplianceForSociety(params)

      setAddNewCompliance(false)
      setNewComplianceData({})

      await fetchData()
      await fetchAvailableCompliances()
    } catch (err) {
      console.error('Error creating compliance:', err)
    }
  }

  return (
    <Card ref={exportRef} sx={{ mt: 4, mb: 4, p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <SectionTitle>
          {t('Compliance')} - {society.legal_id}
        </SectionTitle>

        {!adminMode && (
          <Button
            variant='contained'
            onClick={handleExportPDF}
            sx={{
              backgroundColor: window.$brand === 'PRB' ? '#17afb6' : '#102a46',
              borderRadius: window.$brand === 'PRB' ? 0 : 5
            }}
          >
            {t('Export PDF')}
          </Button>
        )}
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid
          item
          xs={12}
          md={6}
          sx={{ display: 'flex', justifyContent: 'center' }}
        >
          <SocietyComplianceChart compliances={compliances} />
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          sx={{ display: 'flex', justifyContent: 'center' }}
        >
          <SocietyCompletionChart compliances={compliances} />
        </Grid>
      </Grid>
      {adminMode && (
        <Card sx={{ mb: 3, p: 2 }} elevation={0}>
          <Typography variant='subtitle1' gutterBottom>
            {t('Assign new compliance')}
          </Typography>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={8}>
              <Autocomplete
                options={availableCompliances}
                getOptionLabel={(option) => {
                  return society.language === 'es'
                    ? option.name_es
                    : option.name_en
                }}
                value={selectedCompliance}
                onChange={(e, newValue) => setSelectedCompliance(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('Select compliance')}
                    variant='standard'
                  />
                )}
              />
            </Grid>
            <Grid item xs={4} gap={2} container>
              <Button
                variant='contained'
                disabled={!selectedCompliance}
                onClick={handleAssign}
                sx={{
                  backgroundColor:
                    window.$brand === 'PRB' ? '#17afb6' : '#102a46',
                  borderRadius: window.$brand === 'PRB' ? 0 : 5
                }}
              >
                {t('Assign')}
              </Button>
              <Button
                variant='contained'
                onClick={() => setAddNewCompliance(true)}
              >
                {t('Create & Assign')}
              </Button>
            </Grid>
          </Grid>
        </Card>
      )}

      {/* Listado de cumplimientos */}
      <Grid container spacing={2}>
        {compliances &&
          compliances.map((c) => (
            <Grid item xs={12} md={6} key={c.id}>
              <Card>
                <CardContent sx={{ pb: 0 }}>
                  <Typography variant='subtitle1'>
                    <strong>
                      {society.language === 'es' ? c.name_es : c.name_en}
                    </strong>
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {society.language === 'es'
                      ? c.description_es
                      : c.description_en || '-'}
                  </Typography>
                  <Divider sx={{ my: 2 }} />

                  <Typography variant='body2'>
                    {t('Status')}: &nbsp;
                    <Box
                      component='span'
                      sx={{
                        display: 'inline-block',
                        px: 1,
                        py: 0.3,
                        borderRadius: 1,
                        bgcolor:
                          c.status === 'complete'
                            ? 'success.main'
                            : c.status === 'expired'
                              ? 'error.main'
                              : 'warning.main',
                        color: '#fff',
                        fontSize: '0.75rem',
                        fontWeight: 500
                      }}
                    >
                      {c.status || t('pending')}
                    </Box>
                  </Typography>

                  <Typography variant='body2' sx={{ mt: 1 }}>
                    {t('Due Date')}:{' '}
                    {c.due_date
                      ? new Date(c.due_date).toLocaleDateString()
                      : t('pending')}
                  </Typography>
                </CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1,
                    pb: 1,
                    px: 2
                  }}
                >
                  {adminMode && (
                    <>
                      <span>
                        <CPEditIconButton onClick={() => setEditItem(c)} />
                      </span>
                      <span>
                        <CPDeleteIconButton onClick={() => setDeleteItem(c)} />
                      </span>
                    </>
                  )}
                  <span>
                    <IconButton
                      onClick={() => setDocumentsItem(c)}
                      color='primary'
                      aria-label='documents'
                    >
                      <DescriptionIcon />
                    </IconButton>
                  </span>
                </Box>
              </Card>
            </Grid>
          ))}
      </Grid>

      {/* Edit / Delete solo en modo admin */}
      <ComplianceEditDialog
        open={!!editItem?.id}
        compliance={editItem}
        onClose={() => setEditItem({})}
        onSave={handleUpdate}
      />
      <DeleteDialog
        item={deleteItem}
        title={t('delete_compliance')}
        caption={t('delete_society_compliance_full_caption')}
        targetFunc={handleDelete}
        handleDialogClose={() => setDeleteItem({})}
      />

      <SocietyComplianceDocuments
        open={!!documentsItem?.id}
        onClose={() => setDocumentsItem({})}
        compliance={documentsItem}
        society={society}
        adminMode={adminMode}
      />

      <ComplianceDialog
        open={addNewCompliance}
        compliance={newComplianceData}
        errors={{}}
        onClose={() => {
          setAddNewCompliance(false)
          setNewComplianceData({})
        }}
        onSave={handleCreateAndAssign}
        onChange={(updated) =>
          setNewComplianceData({ ...newComplianceData, ...updated })
        }
      />
    </Card>
  )
}
